import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Wallet, Plus, Send, Sparkles, ArrowDownLeft, ArrowUpRight, Gift, RefreshCw, ShieldAlert, Lock, ArrowUpFromLine, Building2, Clock, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

const TX_LABEL = {
    topup: { label: 'Recarga', icon: ArrowDownLeft, tone: 'text-emerald-600' },
    payment: { label: 'Pago de orden', icon: ArrowUpRight, tone: 'text-rose-600' },
    transfer_in: { label: 'Transferencia recibida', icon: ArrowDownLeft, tone: 'text-emerald-600' },
    transfer_out: { label: 'Transferencia enviada', icon: ArrowUpRight, tone: 'text-rose-600' },
    cashback: { label: 'Cashback', icon: Gift, tone: 'text-emerald-600' },
    refund: { label: 'Reembolso', icon: ArrowDownLeft, tone: 'text-emerald-600' },
    points_earn: { label: 'Puntos ganados', icon: Sparkles, tone: 'text-amber-600' },
    points_redeem: { label: 'Canje de puntos', icon: Sparkles, tone: 'text-amber-600' },
    admin_adjustment: { label: 'Ajuste admin', icon: ShieldAlert, tone: 'text-slate-600' },
    moderation: { label: 'Moderación', icon: Lock, tone: 'text-slate-600' },
    reversal: { label: 'Reversión', icon: RefreshCw, tone: 'text-slate-600' },
    withdrawal: { label: 'Retiro', icon: ArrowUpFromLine, tone: 'text-orange-600' },
};

const STATUS_BADGE = {
    pending: <Badge className="bg-amber-100 text-amber-800 border-amber-300"><Clock className="h-3 w-3 mr-1" />Pendiente</Badge>,
    completed: <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300"><CheckCircle2 className="h-3 w-3 mr-1" />Completado</Badge>,
    rejected: <Badge className="bg-red-100 text-red-800 border-red-300"><XCircle className="h-3 w-3 mr-1" />Rechazado</Badge>,
};

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DashboardWallet() {
    const [wallet, setWallet] = useState(null);
    const [txs, setTxs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [txPage, setTxPage] = useState({ items: [], total: 0 });
    const [topupOpen, setTopupOpen] = useState(false);
    const [transferOpen, setTransferOpen] = useState(false);
    const [redeemOpen, setRedeemOpen] = useState(false);
    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [bankAccountOpen, setBankAccountOpen] = useState(false);

    const [topup, setTopup] = useState({ amount: '', method: 'card' });
    const [tf, setTf] = useState({ to_nickname: '', amount: '', currency: 'mxn', note: '' });
    const [rd, setRd] = useState({ points: '' });
    const [wd, setWd] = useState({ amount: '', notes: '' });
    const [bankAccount, setBankAccount] = useState(null);
    const [bankForm, setBankForm] = useState({ clabe: '', bank_name: '', account_holder: '' });
    const [withdrawals, setWithdrawals] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: me }, { data: page }, { data: ba }, { data: wds }] = await Promise.all([
                api.get('/wallet/me'),
                api.get('/wallet/me/transactions', { params: { limit: 100 } }),
                api.get('/wallet/bank-account'),
                api.get('/wallet/withdraw-requests'),
            ]);
            setWallet(me.wallet);
            setTxs(me.recent_transactions || []);
            setTxPage(page || { items: [], total: 0 });
            setBankAccount(ba.bank_account || null);
            if (ba.bank_account) setBankForm(ba.bank_account);
            setWithdrawals(Array.isArray(wds) ? wds : []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al cargar OkWallet');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const doTopup = async () => {
        setBusy(true);
        try {
            await api.post('/wallet/topup', { amount: parseFloat(topup.amount), method: topup.method });
            toast.success('¡Saldo recargado exitosamente!');
            setTopupOpen(false);
            setTopup({ amount: '', method: 'card' });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    const doTransfer = async () => {
        setBusy(true);
        try {
            await api.post('/wallet/transfer', { ...tf, amount: parseFloat(tf.amount) });
            toast.success(`Enviado a @${tf.to_nickname}`);
            setTransferOpen(false);
            setTf({ to_nickname: '', amount: '', currency: 'mxn', note: '' });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    const doRedeem = async () => {
        setBusy(true);
        try {
            await api.post('/wallet/redeem-points', { points: parseFloat(rd.points) });
            toast.success('Puntos canjeados a saldo');
            setRedeemOpen(false);
            setRd({ points: '' });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    const doWithdraw = async () => {
        setBusy(true);
        try {
            await api.post('/wallet/withdraw-request', { amount: parseFloat(wd.amount), notes: wd.notes });
            toast.success('Solicitud de retiro enviada. El equipo de Okgo procesará tu transferencia en 1-3 días hábiles.');
            setWithdrawOpen(false);
            setWd({ amount: '', notes: '' });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    const saveBankAccount = async () => {
        setBusy(true);
        try {
            await api.put('/wallet/bank-account', bankForm);
            toast.success('Cuenta bancaria guardada');
            setBankAccountOpen(false);
            setBankAccount(bankForm);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar cuenta'); } finally { setBusy(false); }
    };

    if (loading) return <div className="p-6 text-sm text-muted-foreground">Cargando OkWallet…</div>;

    return (
        <div className="space-y-6" data-testid="dashboard-okwallet">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Wallet className="h-6 w-6 text-[hsl(var(--primary))]" />
                    <h1 className="text-2xl font-semibold">OkWallet</h1>
                    {wallet?.frozen && <Badge className="bg-rose-100 text-rose-800"><Lock className="h-3 w-3 mr-1" />Congelada</Badge>}
                </div>
                <Button variant="outline" size="sm" onClick={load} data-testid="wallet-refresh">
                    <RefreshCw className="h-4 w-4 mr-1" />Actualizar
                </Button>
            </div>

            {/* Balances */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-[hsl(var(--primary))] to-red-700" />
                    <CardContent className="p-6">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Saldo disponible</div>
                        <div className="text-4xl font-semibold mt-1" data-testid="wallet-balance-mxn">$ {fmt(wallet?.balance_mxn)}<span className="text-lg text-muted-foreground ml-1">MXN</span></div>
                        <div className="flex gap-2 mt-4 flex-wrap">
                            <Button size="sm" onClick={() => setTopupOpen(true)} data-testid="wallet-topup-btn" disabled={wallet?.frozen} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
                                <Plus className="h-4 w-4 mr-1" />Recargar saldo
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setTransferOpen(true)} data-testid="wallet-transfer-btn" disabled={wallet?.frozen}>
                                <Send className="h-4 w-4 mr-1" />Transferir
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setWithdrawOpen(true)} disabled={wallet?.frozen} data-testid="wallet-withdraw-btn">
                                <ArrowUpFromLine className="h-4 w-4 mr-1" />Retirar
                            </Button>
                        </div>
                    </CardContent>
                </Card>
                <Card className="overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-amber-400 to-amber-600" />
                    <CardContent className="p-6">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Sparkles className="h-3 w-3" />Puntos</div>
                        <div className="text-4xl font-semibold mt-1" data-testid="wallet-balance-points">{fmt(wallet?.points)} <span className="text-lg text-muted-foreground">pts</span></div>
                        <div className="text-xs text-muted-foreground mt-1">Equivalen a ${fmt(wallet?.points_value_mxn)} MXN · 1 pt = $1</div>
                        <div className="flex gap-2 mt-4">
                            <Button size="sm" variant="outline" onClick={() => setRedeemOpen(true)} data-testid="wallet-redeem-btn" disabled={!wallet?.points || wallet?.frozen}>
                                <Gift className="h-4 w-4 mr-1" />Canjear a saldo
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Bank account card */}
            <Card className={`border-dashed ${bankAccount ? 'bg-emerald-50/50 border-emerald-300' : 'bg-amber-50/50 border-amber-300'}`}>
                <CardContent className="p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <Building2 className={`h-5 w-5 ${bankAccount ? 'text-emerald-600' : 'text-amber-600'}`} />
                            <div>
                                <div className="text-sm font-semibold">{bankAccount ? 'Cuenta bancaria registrada' : 'Registra tu cuenta bancaria'}</div>
                                {bankAccount ? (
                                    <div className="text-xs text-muted-foreground">
                                        {bankAccount.bank_name} · {bankAccount.account_holder} · CLABE {bankAccount.clabe.slice(0, 6)}…{bankAccount.clabe.slice(-4)}
                                    </div>
                                ) : (
                                    <div className="text-xs text-amber-700">Necesitas tu CLABE para solicitar retiros</div>
                                )}
                            </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setBankAccountOpen(true)} data-testid="wallet-bank-account-btn">
                            <Building2 className="h-4 w-4 mr-1" />{bankAccount ? 'Editar cuenta' : 'Agregar cuenta'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Info card */}
            <Card className="bg-[hsl(var(--accent))]/40 border-dashed">
                <CardContent className="p-4 text-sm">
                    <div className="font-medium mb-1 flex items-center gap-1"><Sparkles className="h-4 w-4" />¿Cómo funciona OkWallet?</div>
                    <ul className="text-muted-foreground list-disc pl-5 space-y-1">
                        <li><strong>Recarga</strong> saldo con tarjeta, OXXO o SPEI.</li>
                        <li><strong>Paga</strong> tus compras usando tu saldo sin comisiones.</li>
                        <li><strong>Transfiere</strong> a otros usuarios por su @nickname (0% comisión).</li>
                        <li>Gana <strong>puntos</strong> por compras y cashback; canjéalos como saldo (1 pt = $1).</li>
                        <li><strong>Retira</strong> tu saldo a tu cuenta bancaria (CLABE) en 1-3 días hábiles.</li>
                    </ul>
                </CardContent>
            </Card>

            {/* Transactions + Withdrawals */}
            <Tabs defaultValue="all">
                <TabsList className="flex-wrap h-auto gap-1">
                    <TabsTrigger value="all" data-testid="wallet-tab-all">Todas ({txPage.total})</TabsTrigger>
                    <TabsTrigger value="topup">Recargas</TabsTrigger>
                    <TabsTrigger value="transfer">Transferencias</TabsTrigger>
                    <TabsTrigger value="points">Puntos</TabsTrigger>
                    <TabsTrigger value="retiros" data-testid="wallet-tab-retiros">Retiros ({withdrawals.length})</TabsTrigger>
                </TabsList>
                {[['all', null], ['topup', 'topup'], ['transfer', ['transfer_in', 'transfer_out']], ['points', ['points_earn', 'points_redeem']]].map(([key, filter]) => (
                    <TabsContent value={key} key={key} className="pt-4 space-y-2">
                        {(txPage.items || []).filter(t => !filter ? true : Array.isArray(filter) ? filter.includes(t.type) : t.type === filter).map(t => {
                            const meta = TX_LABEL[t.type] || { label: t.type, icon: Wallet, tone: 'text-slate-600' };
                            const Icon = meta.icon;
                            const sign = ['transfer_out', 'payment', 'points_redeem', 'withdrawal'].includes(t.type) ? '-' : '+';
                            return (
                                <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid={`wallet-tx-${t.id}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className={`h-9 w-9 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center ${meta.tone}`}><Icon className="h-4 w-4" /></div>
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{meta.label}</div>
                                            <div className="text-xs text-muted-foreground truncate">{t.description || '—'}</div>
                                            <div className="text-[10px] text-muted-foreground">{new Date(t.created_at).toLocaleString('es-MX')}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`text-sm font-semibold ${meta.tone}`}>{sign}{t.currency === 'points' ? fmt(t.amount) + ' pts' : '$' + fmt(t.amount)}</div>
                                        {t.status === 'reversed' && <Badge variant="outline" className="text-xs">Revertida</Badge>}
                                        {t.status === 'pending' && <Badge variant="outline" className="text-xs text-amber-600">Pendiente</Badge>}
                                    </div>
                                </div>
                            );
                        })}
                        {(txPage.items || []).length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Sin movimientos aún.</div>}
                    </TabsContent>
                ))}
                <TabsContent value="retiros" className="pt-4 space-y-3">
                    <div className="text-sm text-muted-foreground">Historial de solicitudes de retiro a tu cuenta bancaria.</div>
                    {withdrawals.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center border border-dashed rounded-xl">No tienes solicitudes de retiro aún.</div>
                    ) : withdrawals.map(w => (
                        <div key={w.id} className="rounded-xl border p-3 bg-card space-y-1">
                            <div className="flex items-center justify-between">
                                <span className="font-semibold text-orange-700">${fmt(w.amount)} MXN</span>
                                {STATUS_BADGE[w.status] || <Badge variant="outline">{w.status}</Badge>}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {w.bank_account?.bank_name} · CLABE {w.bank_account?.clabe?.slice(0,6)}…{w.bank_account?.clabe?.slice(-4)}
                            </div>
                            {w.notes && <div className="text-xs text-muted-foreground">Nota: {w.notes}</div>}
                            {w.admin_note && <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">Respuesta: {w.admin_note}</div>}
                            <div className="text-[10px] text-muted-foreground">{new Date(w.created_at).toLocaleString('es-MX')}</div>
                        </div>
                    ))}
                </TabsContent>
            </Tabs>

            {/* Top-up dialog */}
            <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
                <DialogContent data-testid="wallet-topup-dialog">
                    <DialogHeader><DialogTitle>Recargar OkWallet</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Monto (MXN)</Label>
                            <Input type="number" min="1" max="200000" value={topup.amount} onChange={(e) => setTopup({ ...topup, amount: e.target.value })} placeholder="500" data-testid="wallet-topup-amount" />
                        </div>
                        <div><Label>Método de pago</Label>
                            <Select value={topup.method} onValueChange={(v) => setTopup({ ...topup, method: v })}>
                                <SelectTrigger data-testid="wallet-topup-method"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="card">Tarjeta de crédito / débito</SelectItem>
                                    <SelectItem value="oxxo">OXXO Pay</SelectItem>
                                    <SelectItem value="spei">SPEI / Transferencia</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
                            El saldo se acredita a tu OkWallet de forma inmediata.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setTopupOpen(false)}>Cancelar</Button>
                        <Button onClick={doTopup} disabled={busy || !topup.amount} data-testid="wallet-topup-confirm">{busy ? 'Procesando…' : 'Recargar'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Transfer dialog */}
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                <DialogContent data-testid="wallet-transfer-dialog">
                    <DialogHeader><DialogTitle>Transferir a otro usuario</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Destinatario (@nickname)</Label>
                            <Input value={tf.to_nickname} onChange={(e) => setTf({ ...tf, to_nickname: e.target.value.replace('@', '') })} placeholder="carlos_mtz" data-testid="wallet-transfer-to" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Monto</Label>
                                <Input type="number" min="1" value={tf.amount} onChange={(e) => setTf({ ...tf, amount: e.target.value })} data-testid="wallet-transfer-amount" />
                            </div>
                            <div><Label>Moneda</Label>
                                <Select value={tf.currency} onValueChange={(v) => setTf({ ...tf, currency: v })}>
                                    <SelectTrigger data-testid="wallet-transfer-currency"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="mxn">Saldo $MXN</SelectItem>
                                        <SelectItem value="points">Puntos</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div><Label>Nota (opcional)</Label>
                            <Textarea rows={2} value={tf.note} onChange={(e) => setTf({ ...tf, note: e.target.value })} />
                        </div>
                        <div className="text-xs text-muted-foreground">Sin comisión de plataforma.</div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setTransferOpen(false)}>Cancelar</Button>
                        <Button onClick={doTransfer} disabled={busy || !tf.to_nickname || !tf.amount} data-testid="wallet-transfer-confirm">{busy ? 'Enviando…' : 'Transferir'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Redeem dialog */}
            <Dialog open={redeemOpen} onOpenChange={setRedeemOpen}>
                <DialogContent data-testid="wallet-redeem-dialog">
                    <DialogHeader><DialogTitle>Canjear puntos a saldo</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm text-muted-foreground">Tienes <strong>{fmt(wallet?.points)}</strong> pts disponibles</div>
                        <div><Label>Puntos a canjear</Label>
                            <Input type="number" min="1" max={wallet?.points} value={rd.points} onChange={(e) => setRd({ points: e.target.value })} data-testid="wallet-redeem-amount" />
                            <div className="text-xs text-muted-foreground mt-1">Recibirás ${fmt((parseFloat(rd.points) || 0))} MXN de saldo.</div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setRedeemOpen(false)}>Cancelar</Button>
                        <Button onClick={doRedeem} disabled={busy || !rd.points} data-testid="wallet-redeem-confirm">{busy ? 'Canjeando…' : 'Canjear'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Withdraw dialog */}
            <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
                <DialogContent data-testid="wallet-withdraw-dialog">
                    <DialogHeader><DialogTitle><ArrowUpFromLine className="h-5 w-5 inline mr-2 text-orange-600" />Solicitar retiro</DialogTitle></DialogHeader>
                    {!bankAccount ? (
                        <Alert className="border-amber-200 bg-amber-50">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                Primero debes registrar tu cuenta bancaria (CLABE) para poder retirar.{' '}
                                <button className="underline font-medium" onClick={() => { setWithdrawOpen(false); setBankAccountOpen(true); }}>Registrar ahora</button>
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <div className="space-y-3">
                            <div className="rounded-lg border bg-secondary/30 p-2 text-xs">
                                <div className="font-medium">Cuenta de destino</div>
                                <div>{bankAccount.bank_name} · {bankAccount.account_holder}</div>
                                <div className="font-mono">CLABE: {bankAccount.clabe.slice(0,6)}…{bankAccount.clabe.slice(-4)}</div>
                            </div>
                            <div><Label>Monto a retirar (MXN)</Label>
                                <Input type="number" min="10" max={wallet?.balance_mxn} value={wd.amount}
                                    onChange={(e) => setWd({ ...wd, amount: e.target.value })} placeholder="500" data-testid="wallet-withdraw-amount" />
                                <div className="text-xs text-muted-foreground mt-1">Saldo disponible: ${fmt(wallet?.balance_mxn)} MXN</div>
                            </div>
                            <div><Label>Notas para el equipo (opcional)</Label>
                                <Textarea rows={2} value={wd.notes} onChange={(e) => setWd({ ...wd, notes: e.target.value })} placeholder="Ej: Retiro de ventas de la semana" />
                            </div>
                            <Alert className="border-amber-200 bg-amber-50">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription className="text-xs">
                                    El retiro se procesará en <strong>1-3 días hábiles</strong>. El saldo se deducirá inmediatamente y se reembolsará si la solicitud es rechazada.
                                </AlertDescription>
                            </Alert>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setWithdrawOpen(false)}>Cancelar</Button>
                        {bankAccount && (
                            <Button onClick={doWithdraw} disabled={busy || !wd.amount} className="bg-orange-600 hover:bg-orange-700" data-testid="wallet-withdraw-confirm">
                                {busy ? 'Procesando…' : 'Solicitar retiro'}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bank account dialog */}
            <Dialog open={bankAccountOpen} onOpenChange={setBankAccountOpen}>
                <DialogContent data-testid="wallet-bank-account-dialog">
                    <DialogHeader><DialogTitle><Building2 className="h-5 w-5 inline mr-2 text-blue-600" />Cuenta bancaria (CLABE)</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>CLABE interbancaria (18 dígitos)</Label>
                            <Input value={bankForm.clabe} onChange={(e) => setBankForm({ ...bankForm, clabe: e.target.value.replace(/\D/g, '').slice(0, 18) })}
                                placeholder="123456789012345678" maxLength={18} data-testid="wallet-clabe" />
                        </div>
                        <div><Label>Banco</Label>
                            <Input value={bankForm.bank_name} onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                                placeholder="BBVA, Santander, Banorte, HSBC…" data-testid="wallet-bank-name" />
                        </div>
                        <div><Label>Nombre del titular de la cuenta</Label>
                            <Input value={bankForm.account_holder} onChange={(e) => setBankForm({ ...bankForm, account_holder: e.target.value })}
                                placeholder="Nombre completo tal como aparece en el banco" data-testid="wallet-account-holder" />
                        </div>
                        <div className="text-xs text-muted-foreground bg-slate-50 border rounded p-2">
                            Tu cuenta bancaria solo se usa para procesar tus retiros. No compartimos tus datos con terceros.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setBankAccountOpen(false)}>Cancelar</Button>
                        <Button onClick={saveBankAccount} disabled={busy || bankForm.clabe.length !== 18 || !bankForm.bank_name || !bankForm.account_holder} data-testid="wallet-bank-account-save">
                            {busy ? 'Guardando…' : 'Guardar cuenta'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
